import { useState, useRef, useCallback, useEffect } from "react"
import {
  Upload,
  MapPin,
  ArrowRight,
  X,
  Search,
  ChevronDown,
  Bell,
  Clock,
  Sparkles,
  Briefcase,
  GraduationCap,
  TrendingUp,
  Star,
  ExternalLink,
  Mail,
  CheckCircle2,
  Loader2,
} from "lucide-react"
import Header from "../components/Header"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthProvider"
import UserService from "../services/UserService"
import { doc, setDoc } from "firebase/firestore"
import { db } from "../services/firebase"
import AuthModal from "../components/AuthModal"
import { sendEmail } from "../services/emailService"

const BASE_URL = "https://hire100x-e1dd4.uc.r.appspot.com"

// ─────────────────────────────────────────────────────────────────────────────
const INDIAN_CITIES = [
  "Remote",
  "Pan India",
  "Mumbai",
  "Delhi",
  "Bengaluru",
  "Hyderabad",
  "Chennai",
  "Kolkata",
  "Pune",
  "Ahmedabad",
  "Jaipur",
  "Surat",
  "Lucknow",
  "Kanpur",
  "Nagpur",
  "Indore",
  "Thane",
  "Bhopal",
  "Visakhapatnam",
  "Patna",
  "Vadodara",
  "Ghaziabad",
  "Ludhiana",
  "Agra",
  "Nashik",
  "Faridabad",
  "Meerut",
  "Rajkot",
  "Varanasi",
  "Srinagar",
  "Aurangabad",
  "Dhanbad",
  "Amritsar",
  "Allahabad",
  "Ranchi",
  "Howrah",
  "Coimbatore",
  "Guwahati",
  "Chandigarh",
  "Kochi",
  "Noida",
  "Gurugram",
  "Navi Mumbai",
]

const JOB_BOARDS = [
  { name: "LinkedIn", color: "#0A66C2" },
  { name: "Naukri", color: "#FF7555" },
  { name: "Indeed", color: "#003A9B" },
  { name: "AngelList", color: "#000000" },
  { name: "Glassdoor", color: "#0CAA41" },
  { name: "Unstop", color: "#7C3AED" },
  { name: "Internshala", color: "#06B6D4" },
  { name: "Shine", color: "#E11D48" },
  { name: "Monster", color: "#6554C0" },
  { name: "Wellfound", color: "#FB923C" },
  { name: "Cutshort", color: "#10B981" },
  { name: "HackerEarth", color: "#2563EB" },
  { name: "Hirist", color: "#DC2626" },
]

// ─────────────────────────────────────────────────────────────────────────────
// ATOMS
// ─────────────────────────────────────────────────────────────────────────────
const LiveDot = () => (
  <span className="relative flex h-2 w-2 flex-shrink-0">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
  </span>
)

// ─────────────────────────────────────────────────────────────────────────────
// JOB BOARD PILLS
// ─────────────────────────────────────────────────────────────────────────────
const JobBoardPills = () => (
  <div className="flex flex-wrap gap-2 mt-5">
    {JOB_BOARDS.map((board) => (
      <div
        key={board.name}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-100 bg-white shadow-sm text-[11px] font-semibold text-gray-600 hover:border-gray-300 transition-colors cursor-default"
      >
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: board.color }}
        />
        {board.name}
      </div>
    ))}
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-dashed border-gray-200 text-[11px] text-gray-400">
      +27 more
    </div>
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATED STEPS
// ─────────────────────────────────────────────────────────────────────────────
const STEP_DEFS = [
  {
    n: "01",
    label: "Upload Your Resume",
    sub: "Drop your CV in any format. Our parser reads every line — skills, impact, context — not just keywords.",
    Icon: () => (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    n: "02",
    label: "Define Search Parameters",
    sub: "Pick your city, your role type, your preferred salary. We tailor every result to where you actually want to work.",
    Icon: () => <MapPin size={14} strokeWidth={2.5} />,
  },
  {
    n: "03",
    label: "Fetch Job Opportunities",
    sub: "We scan LinkedIn, Naukri, AngelList, Indeed + 36 more boards — all at once, in real time.",
    Icon: () => (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    n: "04",
    label: "Get Notified Instantly",
    sub: "A new role drops anywhere on the internet. You know in seconds — before anyone else applies.",
    live: true,
    Icon: () => <Bell size={14} strokeWidth={2.5} />,
  },
]

const AnimatedSteps = () => {
  const [active, setActive] = useState(0)
  const [done, setDone] = useState([])
  const [fill, setFill] = useState(0)
  const finished = done.length === STEP_DEFS.length

  useEffect(() => {
    if (finished) return
    setFill(0)
    const TICK_MS = 18,
      TOTAL_MS = 1800
    let current = 0
    const interval = setInterval(() => {
      current += (TICK_MS / TOTAL_MS) * 100
      if (current >= 100) {
        clearInterval(interval)
        setFill(100)
        setTimeout(() => {
          setDone((prev) => [...prev, active])
          if (active < STEP_DEFS.length - 1) {
            setActive((prev) => prev + 1)
            setFill(0)
          }
        }, 200)
      } else {
        setFill(current)
      }
    }, TICK_MS)
    return () => clearInterval(interval)
  }, [active, finished])

  return (
    <div className="w-full">
      {STEP_DEFS.map((step, i) => {
        const isDone = done.includes(i),
          isActive = active === i,
          isLast = i === STEP_DEFS.length - 1
        return (
          <div key={i} className="flex gap-5">
            <div className="flex flex-col items-center" style={{ width: 36 }}>
              <div
                className={`relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isDone ? "bg-black" : isActive ? "bg-black shadow-lg shadow-black/20" : "bg-white border-2 border-gray-200"}`}
              >
                <span
                  className={
                    isDone || isActive ? "text-white" : "text-gray-400"
                  }
                >
                  <step.Icon />
                </span>
                {isActive && (
                  <>
                    <span className="absolute inset-0 rounded-full animate-ping bg-black/10" />
                    <span className="absolute -inset-1 rounded-full border border-black/20 animate-pulse" />
                  </>
                )}
              </div>
              {!isLast && (
                <div
                  className="relative flex-1 my-1"
                  style={{ width: 2, minHeight: 40 }}
                >
                  <div className="absolute inset-0 bg-gray-100 rounded-full" />
                  {(isDone || isActive) && (
                    <div
                      className="absolute top-0 left-0 w-full bg-black rounded-full transition-none"
                      style={{ height: isDone ? "100%" : `${fill}%` }}
                    />
                  )}
                </div>
              )}
            </div>
            <div className={`pb-8 ${isLast ? "pb-0" : ""} flex-1 min-w-0`}>
              <div className="flex items-center gap-2 mt-1.5 mb-1">
                <span
                  className={`text-[9px] font-black tracking-[0.22em] uppercase transition-colors duration-300 ${isActive ? "text-black" : isDone ? "text-gray-400" : "text-gray-300"}`}
                >
                  {step.n}
                </span>
                {step.live && isActive && (
                  <span className="flex items-center gap-1 text-[9px] font-black tracking-wider uppercase text-emerald-600">
                    <LiveDot /> Live
                  </span>
                )}
              </div>
              <p
                className={`font-black leading-snug transition-all duration-400 ${isActive ? "text-base text-black" : isDone ? "text-sm text-gray-400" : "text-sm text-gray-300"}`}
              >
                {step.label}
              </p>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${isActive ? "max-h-20 opacity-100 mt-1.5" : "max-h-0 opacity-0"}`}
              >
                <p className="text-xs text-gray-500 leading-relaxed">
                  {step.sub}
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION BLOCK
// ─────────────────────────────────────────────────────────────────────────────
const SidebarSection = ({ icon: Icon, label, children }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-2.5">
      <Icon size={11} className="text-gray-400 flex-shrink-0" />
      <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-gray-400">
        {label}
      </p>
    </div>
    {children}
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// RESUME SUMMARY PANEL — left 60%
// ─────────────────────────────────────────────────────────────────────────────
const ResumeSummaryPanel = ({ summary, city, role, userEmail, onReset }) => {
  const hasSocialLinks =
    summary.socialLinks &&
    (summary.socialLinks.github ||
      summary.socialLinks.linkedin ||
      summary.socialLinks.leetcode)

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-400 mb-1">
            Your Profile
          </p>
          <h2 className="text-xl font-black text-gray-900 tracking-tight leading-tight truncate">
            {summary.name}
          </h2>
          {summary.headline && (
            <p className="text-sm text-gray-500 mt-0.5 font-medium leading-snug">
              {summary.headline}
            </p>
          )}
          {summary.currentCompany && (
            <p className="text-xs text-gray-400 mt-0.5">
              at {summary.currentCompany}
            </p>
          )}
        </div>
        <button
          onClick={onReset}
          className="text-[11px] font-semibold text-gray-400 hover:text-gray-900 flex items-center gap-1 border border-gray-200 hover:border-gray-400 px-2.5 py-1.5 rounded-lg transition-all flex-shrink-0 ml-3"
        >
          <X size={10} /> Reset
        </button>
      </div>

      {/* Search criteria */}
      <div className="pb-5 border-b border-gray-100">
        <p className="text-[11px] font-black tracking-[0.18em] uppercase text-gray-400 mb-2.5">
          Searching for
        </p>
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 bg-gray-100 px-2.5 py-1 rounded-full">
            <Briefcase size={11} className="text-gray-500" />
            {role}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 bg-gray-100 px-2.5 py-1 rounded-full">
            <MapPin size={11} className="text-gray-500" />
            {city}
          </span>
          {summary.experience && (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 bg-gray-100 px-2.5 py-1 rounded-full">
              <Clock size={11} className="text-gray-500" />
              {summary.experience}
            </span>
          )}
        </div>
      </div>

      {/* Skills */}
      {summary.skills?.length > 0 && (
        <SidebarSection icon={Sparkles} label="Skills">
          <div className="flex flex-wrap gap-1.5">
            {summary.skills.map((s) => (
              <span
                key={s}
                className="text-sm font-semibold text-gray-700 bg-white border border-gray-200 shadow-sm px-3 py-1 rounded-full hover:border-gray-400 transition-colors cursor-default"
              >
                {s}
              </span>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Work History */}
      {summary.workHistory?.length > 0 && (
        <SidebarSection icon={Briefcase} label="Work History">
          <div className="flex flex-col gap-3">
            {summary.workHistory.map((w, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 leading-snug">
                    {w.role}
                  </p>
                  <p className="text-sm text-gray-500">{w.companyName}</p>
                  {(w.startYear || w.endYear) && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {[w.startMonth, w.startYear].filter(Boolean).join(" ")}
                      {" — "}
                      {w.isCurrentJob
                        ? "Present"
                        : [w.endMonth, w.endYear].filter(Boolean).join(" ")}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Education */}
      {summary.education?.length > 0 && (
        <SidebarSection icon={GraduationCap} label="Education">
          <div className="flex flex-col gap-3">
            {summary.education.map((e, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 leading-snug">
                    {e.institute}
                  </p>
                  <p className="text-sm text-gray-500">
                    {[e.degree, e.field].filter(Boolean).join(", ")}
                  </p>
                  {(e.startYear || e.endYear) && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {[e.startMonth, e.startYear].filter(Boolean).join(" ")}
                      {" — "}
                      {[e.endMonth, e.endYear].filter(Boolean).join(" ")}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Projects */}
      {summary.projects?.length > 0 && (
        <SidebarSection icon={TrendingUp} label="Projects">
          <div className="flex flex-col gap-3">
            {summary.projects.map((p, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 leading-snug">
                    {p.title}
                  </p>
                  {p.description && (
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5 line-clamp-2">
                      {p.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Achievements */}
      {summary.achievements?.length > 0 && (
        <SidebarSection icon={Star} label="Achievements">
          <div className="flex flex-col gap-2.5">
            {summary.achievements.map((a, i) => (
              <div key={i} className="flex gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  {a.title && (
                    <p className="text-sm font-semibold text-gray-800 leading-snug">
                      {a.title}
                    </p>
                  )}
                  {a.achievement && (
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
                      {a.achievement}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SidebarSection>
      )}

      {/* Social Links */}
      {hasSocialLinks && (
        <SidebarSection icon={ExternalLink} label="Profiles">
          <div className="flex flex-col gap-2">
            {summary.socialLinks.linkedin && (
              <a
                href={summary.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
              >
                <span className="w-5 h-5 rounded bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-[#0A66C2]"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </span>
                <span className="truncate">LinkedIn</span>
              </a>
            )}
            {summary.socialLinks.github && (
              <a
                href={summary.socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
              >
                <span className="w-5 h-5 rounded bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-gray-800"
                  >
                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                  </svg>
                </span>
                <span className="truncate">GitHub</span>
              </a>
            )}
            {summary.socialLinks.leetcode && (
              <a
                href={summary.socialLinks.leetcode}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
              >
                <span className="w-5 h-5 rounded bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors">
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-[#FFA116]"
                  >
                    <path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z" />
                  </svg>
                </span>
                <span className="truncate">LeetCode</span>
              </a>
            )}
          </div>
        </SidebarSection>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL NOTIFICATION PANEL — right 40%, sticky
// ─────────────────────────────────────────────────────────────────────────────
const EmailNotificationPanel = ({ summary, city, role, userEmail, userId }) => {
  const [emailStage, setEmailStage] = useState("finding")

  useEffect(() => {
    handleSend()
  }, [])

  const handleSend = async () => {
    try {
      let matches = []
      try {
        const res = await fetch(`${BASE_URL}/network/jobs/${userId}`)
        if (res.ok) {
          const json = await res.json()
          matches = json?.data?.matches ?? []
        }
      } catch (err) {
        console.warn("Could not fetch jobs:", err)
      }

      setEmailStage("sending")

      const candidateName =
        summary?.name?.split(" ")[0] || userEmail?.split("@")[0] || "there"
      const roleLabel = role ?? "open"
      const matchCount = matches.length
      const plural = matchCount !== 1
      const displayedMatches = matches.slice(0, 5)
      const F =
        "-apple-system,BlinkMacSystemFont,'Helvetica Neue',Helvetica,Arial,sans-serif"

      const stripHtml = (s) =>
        s
          .replace(/<[^>]+>/g, "")
          .replace(/\s+/g, " ")
          .trim()

      // ── Job rows — table layout only, no flex, no calc() ─────────────────────
      const jobRowsHtml = displayedMatches.length
        ? displayedMatches
            .map((j) => {
              const title = j.title || j.role || "Open Role"
              const company = j.company || ""
              const location = j.location || ""
              const meta = [company, location].filter(Boolean).join(" · ")
              const desc = stripHtml(j.description || "").slice(0, 110)
              const logoSrc = j.company_logo || ""
              const applyUrl = j.job_url || "#"
              const initial = (company || title)[0]?.toUpperCase() ?? "?"
              const source = j.source || ""

              const logoCell = logoSrc
                ? `<img src="${logoSrc}" width="36" height="36" style="display:block;width:36px;height:36px;border-radius:6px;border:1px solid #e5e7eb;" />`
                : `<div style="width:36px;height:36px;border-radius:6px;background:#f3f4f6;border:1px solid #e5e7eb;font-size:13px;font-weight:900;color:#9ca3af;text-align:center;line-height:36px;font-family:${F};">${initial}</div>`

              return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom:1px solid #f3f4f6;"><tr>
<td width="36" valign="top" style="padding:14px 10px 14px 0;">${logoCell}</td>
<td valign="top" style="padding:14px 0;">
  <p style="margin:0 0 3px;font-size:13px;font-weight:800;color:#111827;line-height:1.3;font-family:${F};">${title}</p>
  ${meta ? `<p style="margin:0 0 5px;font-size:11px;color:#6b7280;line-height:1.4;font-family:${F};">${meta}</p>` : ""}
  ${desc ? `<p style="margin:0 0 8px;font-size:11px;color:#9ca3af;line-height:1.5;font-family:${F};">${desc}...</p>` : ""}
  <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
    <td style="font-size:9px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#d1d5db;vertical-align:middle;font-family:${F};">${source}</td>
    <td align="right" style="vertical-align:middle;"><a href="${applyUrl}" style="display:inline-block;background:#111827;color:#ffffff;text-decoration:none;font-size:9px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;padding:6px 12px;border-radius:4px;font-family:${F};">Apply &rarr;</a></td>
  </tr></table>
</td>
</tr></table>`
            })
            .join("")
        : `<p style="padding:16px 0;font-size:12px;color:#9ca3af;font-family:${F};">Scanning all job boards — matches will appear here shortly.</p>`

      const moreNote =
        matchCount > 5
          ? `<p style="margin:4px 0 16px;font-size:11px;color:#9ca3af;text-align:center;font-family:${F};">+ ${matchCount - 5} more match${matchCount - 5 !== 1 ? "es" : ""} in your dashboard</p>`
          : ""

      // ── Email HTML ────────────────────────────────────────────────────────────
      const emailHtml = `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<meta name="color-scheme" content="light" />
<meta name="supported-color-schemes" content="light" />
<meta name="format-detection" content="telephone=no,date=no,address=no,email=no" />
<title>${matchCount} ${roleLabel} match${plural ? "es" : ""} - Resolut</title>
<style>
:root { color-scheme: light only; }
body { margin:0; padding:0; background-color:#f3f4f6 !important; }
* { -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
/* Force light mode — block Gmail dark mode recoloring */
[data-ogsc] .dark-ignore { background-color:#ffffff !important; color:#111827 !important; }
u + #body .dark-ignore { background-color:#ffffff !important; }
@media only screen and (max-width:480px) {
  .card { width:100% !important; }
  .e-head { padding:18px 16px 16px !important; }
  .e-h1 { font-size:20px !important; }
  .e-body { padding:0 16px !important; }
  .e-foot { padding:12px 16px !important; }
}
</style>
</head>
<body id="body" style="margin:0;padding:0;background-color:#f3f4f6;">
<div style="background-color:#f3f4f6;padding:24px 12px;">

<table class="card dark-ignore" align="center" width="560" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border:1px solid #e5e7eb;border-radius:6px;width:560px;max-width:100%;">

  <!-- HEADER -->
  <tr><td class="e-head" style="background-color:#111827;padding:22px 24px 18px;border-radius:6px 6px 0 0;">
    <p style="margin:0 0 8px;font-size:9px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#6b7280;font-family:${F};">Resolut &middot; Job Alerts</p>
    <p class="e-h1" style="margin:0 0 8px;font-size:23px;font-weight:900;color:#f9fafb;line-height:1.2;letter-spacing:-0.02em;font-family:${F};">${matchCount} match${plural ? "es" : ""} for you.</p>
    <p style="margin:0;font-size:12px;color:#9ca3af;line-height:1.5;font-family:${F};">Hi ${candidateName} &mdash; <strong style="color:#d1d5db;">${matchCount} ${roleLabel} role${plural ? "s" : ""}</strong> found in <strong style="color:#d1d5db;">${city}</strong>.</p>
  </td></tr>

  <!-- BODY -->
  <tr><td class="e-body dark-ignore" style="background-color:#ffffff;padding:0 24px;">
    <p style="margin:16px 0 4px;font-size:9px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#d1d5db;font-family:${F};">Top matches &mdash; ${roleLabel} &middot; ${city}</p>
    ${jobRowsHtml}
    ${moreNote}
  </td></tr>

  <!-- FOOTER -->
  <tr><td class="e-foot" style="background-color:#f9fafb;padding:14px 24px;border-top:1px solid #f3f4f6;border-radius:0 0 6px 6px;">
    <p style="margin:0 0 5px;font-size:11px;color:#6b7280;line-height:1.6;font-family:${F};"><strong style="color:#374151;">Live alert active.</strong> Every new <strong style="color:#374151;">${roleLabel}</strong> role in <strong style="color:#374151;">${city}</strong> &mdash; you hear first.</p>
    <p style="margin:0;font-size:10px;font-family:${F};"><a href="#" style="color:#9ca3af;text-decoration:underline;">Manage preferences</a> &nbsp;&middot;&nbsp; <a href="#" style="color:#9ca3af;text-decoration:underline;">Unsubscribe</a></p>
  </td></tr>

</table>

</div>
</body>
</html>`

      await sendEmail({
        email: userEmail,
        subject: `${matchCount} ${roleLabel} match${plural ? "es" : ""} in ${city} - Resolut`,
        text: emailHtml,
      })

      setEmailStage("sent")
    } catch (err) {
      console.error("Failed to send job alert email:", err)
      setEmailStage("sent")
    }
  }

  const isSent = emailStage === "sent"

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-400 mb-1">
          What happens next
        </p>
        <h2 className="text-xl font-black text-gray-900 tracking-tight leading-tight">
          {isSent ? "Check your inbox" : "Jobs come to you"}
        </h2>
        <p className="text-sm text-gray-500 mt-1 leading-relaxed">
          {isSent
            ? `We've sent your matches to ${userEmail}. You'll hear from us the moment anything new posts.`
            : `We monitor 40+ boards 24/7. The moment a ${role} role drops in ${city} — you're notified before anyone else applies.`}
        </p>
      </div>

      {/* Alert note — always visible */}
      <div className="border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50/50">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell size={13} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">
              Alerts sent to your inbox
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
              The internet is big. We watch all of it. Nothing matching your
              profile slips through.
            </p>
            {userEmail && (
              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                <span className="text-gray-500">@</span>
                <span className="font-medium text-gray-500">{userEmail}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status button */}
      <div className="flex flex-col gap-2">
        <button
          disabled
          className={`w-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 cursor-default
            ${
              isSent
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-gray-900 text-white"
            }`}
        >
          {emailStage === "finding" && (
            <>
              <Loader2 size={14} className="animate-spin" /> Finding matching
              jobs…
            </>
          )}
          {emailStage === "sending" && (
            <>
              <Loader2 size={14} className="animate-spin" /> Sending email…
            </>
          )}
          {emailStage === "sent" && (
            <>
              <CheckCircle2 size={14} className="text-emerald-500" /> Email
              sent!
            </>
          )}
        </button>

        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <Mail size={10} />
          {isSent ? (
            "Next set will appear as we find any matching job opportunity"
          ) : (
            <>
              <span>Sending to</span>{" "}
              <span className="font-semibold text-gray-500">{userEmail}</span>
            </>
          )}
        </p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// UPLOAD FORM
// ─────────────────────────────────────────────────────────────────────────────
const UploadForm = ({
  onSubmit,
  loadingStage,
  city,
  setCity,
  role,
  setRole,
  onClearIntent,
}) => {
  const isLoading = loadingStage !== null
  const ref = useRef(null)
  const [drag, setDrag] = useState(false)
  const [file, setFile] = useState(null)
  const [q, setQ] = useState("")
  const [open, setOpen] = useState(false)
  const filtered = INDIAN_CITIES.filter((c) =>
    c.toLowerCase().includes(q.toLowerCase()),
  )
  const ready = file && city && role

  const onDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDrag(e.type === "dragenter" || e.type === "dragover")
  }, [])
  const onDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDrag(false)
      const f = e.dataTransfer.files?.[0]
      if (f) {
        setFile(f)
        onClearIntent?.()
      }
    },
    [onClearIntent],
  )

  return (
    <div className="flex flex-col gap-3">
      {/* Resume */}
      <div
        onDragEnter={onDrag}
        onDragOver={onDrag}
        onDragLeave={onDrag}
        onDrop={onDrop}
        onClick={() => !file && ref.current?.click()}
        className={`relative transition-all duration-200 border ${file ? "border-indigo-200 bg-indigo-50/60 cursor-default" : drag ? "border-indigo-300 bg-indigo-50 scale-[1.01] cursor-copy" : "border-dashed border-gray-300 hover:border-indigo-300 hover:bg-gray-50/60 cursor-pointer"}`}
      >
        <input
          ref={ref}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={(e) => {
            setFile(e.target.files?.[0])
            onClearIntent?.()
          }}
          className="hidden"
        />
        {file ? (
          <div className="px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 truncate max-w-[190px]">
                  {file.name}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {(file.size / 1024).toFixed(1)} KB · Ready
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setFile(null)
              }}
              className="w-6 h-6 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition-colors ml-2 flex-shrink-0"
            >
              <X size={11} className="text-indigo-400" />
            </button>
          </div>
        ) : (
          <div className="px-4 py-5 flex items-center gap-3.5">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${drag ? "bg-indigo-100" : "bg-gray-100"}`}
            >
              <Upload
                size={14}
                className={drag ? "text-indigo-500" : "text-gray-400"}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {drag ? "Drop it here" : "Drop your resume"}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                PDF, DOC, DOCX · or{" "}
                <span className="text-indigo-500">browse</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Role */}
      <div className="flex items-center gap-2.5 border border-gray-200 focus-within:border-indigo-300 focus-within:ring-3 focus-within:ring-indigo-50 px-3.5 py-3 transition-all bg-white">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ca3af"
          strokeWidth="2"
          strokeLinecap="round"
          className="flex-shrink-0"
        >
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
        </svg>
        <input
          type="text"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g. Senior Frontend Engineer"
          className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400"
        />
        {role && (
          <button
            onClick={() => setRole("")}
            className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
          >
            <X size={10} className="text-gray-400" />
          </button>
        )}
      </div>

      {/* City */}
      <div className="relative">
        <div
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center gap-2.5 border px-3.5 py-3 cursor-pointer transition-all bg-white ${open ? "border-indigo-300 ring-3 ring-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
        >
          <MapPin size={13} className="text-gray-400 flex-shrink-0" />
          <span
            className={`flex-1 text-sm ${city ? "text-gray-800 font-medium" : "text-gray-400"}`}
          >
            {city || "Where do you want to work?"}
          </span>
          <ChevronDown
            size={13}
            className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? "rotate-180" : ""}`}
          />
        </div>
        {open && (
          <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 shadow-lg shadow-gray-100/80">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search
                  size={12}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search cities..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full pl-8 pr-3 py-2 text-sm outline-none bg-gray-50 focus:bg-white transition-colors placeholder-gray-400 text-gray-700"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto py-1">
              {filtered.map((c) => (
                <div
                  key={c}
                  onClick={() => {
                    setCity(c)
                    setOpen(false)
                    setQ("")
                  }}
                  className={`px-3 py-2.5 text-sm cursor-pointer flex items-center gap-2.5 transition-colors ${city === c ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-600 hover:bg-gray-50"}`}
                >
                  <MapPin
                    size={10}
                    className={city === c ? "text-indigo-400" : "text-gray-300"}
                  />
                  {c}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <button
        onClick={() => ready && !isLoading && onSubmit({ file, city, role })}
        disabled={!ready || isLoading}
        className={`w-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 mt-1 ${loadingStage ? "bg-gray-900 text-white cursor-not-allowed" : ready ? "bg-indigo-500 text-white hover:bg-indigo-600 active:scale-[0.99]" : "bg-gray-100 text-gray-300 cursor-not-allowed"}`}
      >
        {loadingStage ? (
          <>
            <svg
              className="animate-spin h-4 w-4 flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-20"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3.5"
              />
              <path
                className="opacity-80"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            {loadingStage === "uploading" && "Uploading Resume…"}
            {loadingStage === "processing" && "Processing Resume…"}
            {loadingStage === "scanning" && "Scanning 40+ boards…"}
          </>
        ) : (
          <>
            {" "}
            Find My Jobs <ArrowRight size={14} />{" "}
          </>
        )}
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const FindJobRequestPage = () => {
  const navigate = useNavigate()
  const { user, updateUser } = useAuth()

  const [city, setCity] = useState("")
  const [role, setRole] = useState("")
  const [phase, setPhase] = useState("upload")
  const [loadingStage, setLoadingStage] = useState(null)
  const [resumeSummary, setResumeSummary] = useState(null)

  const [showAuth, setShowAuth] = useState(false)
  const [authMode, setAuthMode] = useState("signin")
  const pendingSubmitRef = useRef(null)

  const handleClearIntent = () => {}

  const handleAuthSuccess = useCallback(async () => {
    setShowAuth(false)
    if (pendingSubmitRef.current) {
      const { file, city: c, role: r } = pendingSubmitRef.current
      pendingSubmitRef.current = null
      await runSearch(file, c, r)
    }
  }, [])

  const pollProcessingStatus = (uid) =>
    new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/users/resume/processing-status/${uid}`,
          )
          if (!res.ok) throw new Error("Status check failed")
          const { data } = await res.json()
          if (data?.status === "completed") {
            clearInterval(interval)
            resolve(data.resumeData ?? null)
          } else if (data?.status === "failed") {
            clearInterval(interval)
            reject(new Error(data.error || "Resume processing failed"))
          }
        } catch (err) {
          clearInterval(interval)
          reject(err)
        }
      }, 2000)
    })

  const callNetworkResumeApi = async (
    uid,
    file,
    submittedRole,
    submittedCity,
  ) => {
    try {
      const formData = new FormData()
      formData.append("userId", uid)
      formData.append("desired_roles", submittedRole)
      formData.append("location", submittedCity)
      formData.append("resumeFile", file)
      const res = await fetch(`${BASE_URL}/network/resume`, {
        method: "POST",
        body: formData,
      })
      if (!res.ok) console.warn("POST /network/resume failed:", res.status)
    } catch (err) {
      console.warn("POST /network/resume error:", err)
    }
  }

  const createJobSeekerDoc = async (uid) => {
    try {
      await setDoc(doc(db, "job_seekers", uid), {}, { merge: true })
    } catch (err) {
      console.warn("Failed to create job_seekers doc:", err)
    }
  }

  const runSearch = async (file, submittedCity, submittedRole) => {
    try {
      setLoadingStage("uploading")
      let parsedResume = null

      try {
        const resumeInfo = await UserService.uploadResume(user.uid, file)
        if (resumeInfo) {
          setLoadingStage("processing")
          await Promise.all([
            pollProcessingStatus(user.uid)
              .then((data) => {
                parsedResume = data
                if (parsedResume) {
                  updateUser({
                    ...(parsedResume.personal_information && {
                      personal_information: parsedResume.personal_information,
                    }),
                    ...(parsedResume.work_history && {
                      work_history: parsedResume.work_history,
                    }),
                    ...(parsedResume.education && {
                      education: parsedResume.education,
                    }),
                    ...(parsedResume.projects && {
                      projects: parsedResume.projects,
                    }),
                    ...(parsedResume.achievements && {
                      achievements: parsedResume.achievements,
                    }),
                    ...(parsedResume.social_links && {
                      social_links: parsedResume.social_links,
                    }),
                  }).catch((err) =>
                    console.warn("Failed to update user doc:", err),
                  )
                }
              })
              .catch((err) => console.warn("Resume processing failed:", err)),
            callNetworkResumeApi(user.uid, file, submittedRole, submittedCity),
            createJobSeekerDoc(user.uid),
          ])
        }
      } catch (uploadErr) {
        console.warn("Resume upload failed:", uploadErr)
      }

      setLoadingStage("scanning")
      await new Promise((r) => setTimeout(r, 1800))

      const pi = parsedResume?.personal_information
      const parsedName =
        pi?.name || pi?.fullName || pi?.firstName
          ? [pi?.firstName, pi?.lastName].filter(Boolean).join(" ") ||
            pi?.name ||
            pi?.fullName
          : null
      const summary = {
        name: parsedName || user?.name || "You",
        headline: pi?.currentJobTitle || null,
        experience: pi?.yearsExperience ? `${pi.yearsExperience}` : null,
        currentCompany: pi?.currentCompany || null,
        skills: parsedResume?.skills ?? [],
        education: parsedResume?.education ?? [],
        workHistory: parsedResume?.work_history ?? [],
        projects: parsedResume?.projects ?? [],
        achievements: parsedResume?.achievements ?? [],
        socialLinks: parsedResume?.social_links ?? {},
      }

      updateUser({
        jobSearchParameters: {
          city: submittedCity,
          role: submittedRole,
          updatedAt: new Date().toISOString(),
        },
      }).catch((err) =>
        console.warn("Failed to save job search parameters:", err),
      )

      setResumeSummary(summary)
      setPhase("results")
    } finally {
      setLoadingStage(null)
    }
  }

  const handleSubmit = async ({
    file,
    city: submittedCity,
    role: submittedRole,
  }) => {
    if (!user?.authenticated) {
      pendingSubmitRef.current = {
        file,
        city: submittedCity,
        role: submittedRole,
      }
      setAuthMode("signin")
      setShowAuth(true)
      return
    }
    await runSearch(file, submittedCity, submittedRole)
  }

  return (
    <div className="min-h-screen bg-white font-satoshi flex flex-col">
      <Header />

      {/* ══════════════════ UPLOAD PHASE ══════════════════ */}
      {phase === "upload" && (
        <div
          className="flex-1 flex"
          style={{ minHeight: "calc(100vh - 72px)" }}
        >
          <div className="flex-1 flex md:mx-[9%]">
            {/* LEFT */}
            <div className="w-1/2 flex items-center py-16 pl-12 pr-16">
              <div className="w-full max-w-lg">
                <div className="flex items-center gap-2 mb-7">
                  <LiveDot />
                  <span className="text-[10px] font-black tracking-[0.22em] uppercase text-gray-500">
                    Live · 40+ job boards
                  </span>
                </div>
                <h1 className="text-5xl font-black text-black tracking-tighter leading-[1.05] mb-4">
                  A job gets posted.
                  <br />
                  <span className="text-gray-300">You know first.</span>
                </h1>
                <p className="text-sm text-gray-500 leading-relaxed mb-7">
                  Upload once. We watch the entire internet and surface every
                  relevant role before anyone else finds it.
                </p>
                <JobBoardPills />
                <div className="mt-10">
                  <AnimatedSteps />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="w-1/2 relative flex items-center justify-center py-12 pl-10 pr-12 bg-white border-l border-gray-100">
              <div className="relative z-10 w-full max-w-sm">
                <div className="flex items-center gap-2 mb-6">
                  <LiveDot />
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-gray-400">
                    Scanning now · 40+ boards
                  </span>
                </div>
                <div className="mb-8">
                  <h2 className="text-[30px] font-black text-gray-900 tracking-tight leading-[1.08] mb-3">
                    Start in
                    <br />
                    <span className="relative inline-block">
                      30 seconds.
                      <div className="absolute bottom-1 left-0 right-0 h-[6px] bg-indigo-100 -z-10 rounded-sm" />
                    </span>
                  </h2>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    Upload your resume. We surface every relevant role across
                    the internet — in real time.
                  </p>
                </div>
                <UploadForm
                  onSubmit={handleSubmit}
                  loadingStage={loadingStage}
                  city={city}
                  setCity={setCity}
                  role={role}
                  setRole={setRole}
                  onClearIntent={handleClearIntent}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ RESULTS PHASE ══════════════════ */}
      {phase === "results" && resumeSummary && (
        <div
          className="flex gap-12 py-10 px-[9%] items-start"
          style={{
            paddingLeft: "calc(9% + 4rem)",
            paddingRight: "calc(9% + 4rem)",
          }}
        >
          {/* LEFT — 60% */}
          <div className="w-[60%] flex-shrink-0">
            <ResumeSummaryPanel
              summary={resumeSummary}
              city={city}
              role={role}
              userEmail={user?.email}
              onReset={() => {
                setPhase("upload")
                setResumeSummary(null)
                setCity("")
                setRole("")
              }}
            />
          </div>

          {/* RIGHT — 40% */}
          <div className="w-[40%] flex-shrink-0 pl-8 border-l border-gray-100">
            <EmailNotificationPanel
              summary={resumeSummary}
              city={city}
              role={role}
              userEmail={user?.email}
              userId={user?.uid}
            />
          </div>
        </div>
      )}

      <AuthModal
        isOpen={showAuth}
        onClose={() => {
          setShowAuth(false)
          pendingSubmitRef.current = null
        }}
        onSuccess={handleAuthSuccess}
        initialMode={authMode}
      />
    </div>
  )
}

export default FindJobRequestPage
